/* global PopupController */
import _ from '@bingads-webui-component-legacy/extension/underscore';
import $ from 'jquery';
import Backbone from 'backbone';
import ko from 'knockout';
import generateFieldOptions from '@bingads-webui-component-legacy/form/view/field/generate';
import generateSidebarsOptions from '@bingads-webui-component-legacy/form/view/sidebar/generator';
import tmplSidebarWrapper from '@bingads-webui-component-legacy/form/template/sidebar/wrapper.pug';
import tmplHorizontal from '@bingads-webui-component-legacy/form/template/simple-form/layout/horizontal.pug';
import tmplInline from '@bingads-webui-component-legacy/form/template/simple-form/layout/inline.pug';
import tmplTextInput from '@bingads-webui-component-legacy/form/template/simple-form/control/text-input.pug';
import tmplTextareaInput from '@bingads-webui-component-legacy/form/template/simple-form/control/textarea.pug';
import tmplStaticDiv from '@bingads-webui-component-legacy/form/template/simple-form/control/static.pug';
import tmplSelectInput from '@bingads-webui-component-legacy/form/template/simple-form/control/select-input.pug';
import tmplCheckboxInput from '@bingads-webui-component-legacy/form/template/simple-form/control/checkbox-input.pug';
import tmplRadiosInput from '@bingads-webui-component-legacy/form/template/simple-form/control/radios-input.pug';
import tmplInlineRadiosInput from '@bingads-webui-component-legacy/form/template/simple-form/control/inline-radios-input.pug';
import tmplLabel from '@bingads-webui-component-legacy/form/template/simple-form/control/label.pug';
import tmplButtons from '@bingads-webui-component-legacy/form/template/simple-form/control/buttons.pug';
import tmplValidation from '@bingads-webui-component-legacy/form/template/simple-form/control/validation.pug';
import SimpleFormViewModel from '@bingads-webui-component-legacy/form/viewmodel/simple-form';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!component/form');

function isBackboneView(func = {}) {
  return func.prototype instanceof Backbone.View;
}

export default Backbone.View.extend({
  events() {
    return {
      'click .save': 'save',
      'click .cancel': 'cancel',
      'submit form': 'save',
      // TODO: [zhbliu] remove this out before we open source this component
      'click .helpbutton': 'clickHelpIcon',
    };
  },

  clickHelpIcon(e) {
    /*eslint new-cap: 0*/
    PopupController.PopupJsClick(e.target);
    e.stopPropagation();
  },

  initialize({
    schema,
    fields,
    model,
    customValidators = {},
    sidebars = [],
    template = 'horizontal',
    labelTemplate = tmplLabel,
    hasButtons = true,
    saveButtonText = i18n.get('Form_SaveButton'),
    cancelButtonText = i18n.get('Form_CancelButton'),
    hasLabels = true,
    s$i18n = {
      type(type) {
        return type;
      },
      property(type, property) {
        return property;
      },
      value(type, property, value) {
        return String(value);
      },
    },
  }) {
    if (_.isFunction(template)) {
      this.template = template;
    } else {
      this.template = {
        'horizontal': tmplHorizontal,
        'inline': tmplInline,
      }[template];
    }

    this.model = model;
    this.type = schema.name;
    this.id = `${_.uniqueId('Form-')}-${schema.name}`;

    this.fields = generateFieldOptions(schema, fields, this.id, s$i18n);

    this.flattedFields = this._getFlattedFields(this.fields);

    this.properties = _.chain(this.flattedFields)
      .filter((field) => {
        return field.hasSubFields !== true;
      })
      .pluck('property')
      .value();

    this.sidebars = generateSidebarsOptions({ schema, properties: this.properties, sidebars });

    this.fieldIndex = _.indexBy(this.flattedFields, (field) => field.property);

    this.viewModel = new SimpleFormViewModel({
      fields: this.flattedFields,
      schema,
      model: this.model,
      customValidators,
    });

    _.each(this.properties, (prop) => {
      this.viewModel[prop].subscribe((val) => {
        this.trigger(`change:${prop}`, val);
        this.trigger('change', this.currentModel());
      });
    });

    this.labelTemplate = labelTemplate;
    this.hasButtons = hasButtons;
    this.saveButtonText = saveButtonText;
    this.cancelButtonText = cancelButtonText;
    this.hasLabels = hasLabels;
  },

  render() {
    this.$el.html(this.template({
      properties: this.properties,
      fields: this.fields,
      indexedFields: this.fieldIndex,
      hasButtons: this.hasButtons,
      hasLabels: this.hasLabels,
      type: this.type,
    }));

    this._renderBuiltInControls();
    this._renderTemplateControls();
    this._renderLabels();
    this._renderValidations();

    ko.applyBindings(this.viewModel, this.el);

    this._renderViewControls();
    this._renderSidebars();
    return this;
  },

  remove() {
    _.each(this.viewControls, (viewControl) => {
      viewControl.remove();
    });

    _.each(this.sidebarViews, (sidebarView) => {
      sidebarView.remove();
    });

    Backbone.View.prototype.remove.apply(this);
  },

  _getFlattedFields(fields) {
    let ret = _.map(fields, (field) => {
      let subFields = [];

      if (field.hasSubFields === true) {
        subFields = this._getFlattedFields(field.fields);
      }

      subFields.push(field);
      return subFields;
    });

    return _.flatten(ret);
  },

  _renderBuiltInControls() {
    this.$('.form-input-container').each((i, e) => {
      const $el = $(e);

      $el.html(this._renderBuiltInControl($el.attr('data-property')));
    });
  },

  _renderBuiltInControl(property) {
    if (property === '$buttons') {
      return tmplButtons(_.pick(this, ['saveButtonText', 'cancelButtonText']));
    } else {
      const field = this.fieldIndex[property];
      const observable = this.viewModel[property];

      return _.result({
        text: () => tmplTextInput({
          field,
          hasLengthHint: observable.maxLength && field.showHint,
          hasLengthHintInline: observable.maxLength && field.showHintInline,
          type: this.type,
        }),

        textarea: () => tmplTextareaInput({
          field,
          type: this.type,
        }),

        static: () => tmplStaticDiv({
          field,
          type: this.type,
        }),

        select: () => tmplSelectInput({
          field,
          type: this.type,
        }),

        checkbox: () => tmplCheckboxInput({
          field,
          type: this.type,
        }),

        radios: () => tmplRadiosInput({
          field,
          type: this.type,
        }),

        'inline-radios': () => tmplInlineRadiosInput({
          field,
          type: this.type,
        }),
      }, field.control, '');
    }
  },

  _renderTemplateControls() {
    this.$('.form-input-container').each((i, e) => {
      const $el = $(e);
      const property = $el.attr('data-property');
      const field = this.fieldIndex[property] || {};
      const template = field.control;

      if (!isBackboneView(template) && _.isFunction(template)) {
        $el.html(template({
          field,
          type: this.type,
        }));
      }
    });
  },

  _renderViewControls() {
    this.viewControls = {};

    this.$('.form-input-container').each((i, e) => {
      const $el = $(e);
      const property = $el.attr('data-property');
      const field = this.fieldIndex[property] || {};
      const Constructor = field.control;

      if (isBackboneView(Constructor)) {
        this.viewControls[property] = new Constructor({
          observable: this.viewModel[property],
          viewModel: this.viewModel,
          field,
        });

        $el.append(this.viewControls[property].render().el);
      }
    });
  },

  _renderLabels() {
    this.$('.form-label-container').each((i, e) => {
      const $el = $(e);
      const field = this.fieldIndex[$el.attr('data-property')];

      if (field.showLabel) {
        const learnMoreText = field.schema.helpId ? i18n.get('Form_LearnMore', {
          item: field.label,
        }) : '';

        $el.html(this.labelTemplate({
          field,
          type: this.type,
          learnMoreText,
        }));
      }
    });
  },

  _renderValidations() {
    this.$('.form-validation-container').each((i, e) => {
      const $el = $(e);
      const property = $el.attr('data-property');
      const field = this.fieldIndex[property] || { property, id: `${this.id}-${property}` };
      const observable = this.viewModel[property];

      if (_.isFunction(observable.validate)) {
        $el.html(tmplValidation({ field }));
      }
    });
  },

  _renderSidebars() {
    this.sidebarViews = {};

    _.each(this.sidebars, ({ properties, view: Ctor = {} }, strProperties) => {
      const selector = _.map(properties, (property) => `.form-group[data-property=${property}]`).join(',');

      this.$(selector).wrapAll(tmplSidebarWrapper({ properties }));

      if (isBackboneView(Ctor)) {
        const sidebar = new Ctor({
          className: 'sidebar',
          id: `${this.id}-${strProperties}`,
          properties,
          viewModel: this.viewModel,
        });

        this.$(`.form-groups-with-sidebar[data-properties='${strProperties}']`).append(sidebar.render().el);

        this.sidebarViews[strProperties] = sidebar;
      }
    });
  },

  currentModel() {
    return _.defaults(this.viewModel.toObject(), this.model);
  },

  setErrors(errors, model) {
    const modelErr = model || this.currentModel();

    _.each(errors, (message, prop) => {
      const observable = this.viewModel[prop];

      // TODO: [zhbliu] align these error handling code
      if (_.isArray(message)) {
        const proxy = observable || this.viewModel.$overall;
        const uniqMessage = _.uniq(message, (msg1, msg2) => {
          return msg1.message === msg2.message;
        });

        proxy.validate();
        proxy.errors.push(...uniqMessage);
      } else if (observable && observable.validators) {
        const validator = observable.validators.passive;

        if (validator && _.isFunction(validator.setError)) {
          validator.setError(message, modelErr[prop]);
        }

        observable.validate();
      }
    });
  },

  validate() {
    return this.viewModel.validate();
  },

  save(e) {
    if (e && e.type === 'submit') {
      // Form submission will cause page redirection; stop it here
      e.preventDefault();
    }

    if (this.validate()) {
      this.trigger('save', this.currentModel());
    }
  },

  cancel() {
    this.trigger('cancel', this.currentModel());
  },
});
